import { useEffect, useMemo, useState } from "react";
import { Navigate, Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Building2, User2, Package, Mail, ExternalLink, Search } from "lucide-react";

export default function MyCustomers() {
  const { user } = useAuth();
  const [customers, setCustomers] = useState([]);
  const [assets, setAssets] = useState([]);
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) return;
    Promise.all([api.get("/customers"), api.get("/assets")])
      .then(([c, a]) => { setCustomers(c.data); setAssets(a.data); if (c.data.length > 0) setSelected(c.data[0]); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [user]);

  const filtered = useMemo(() => {
    const term = q.toLowerCase();
    if (!term) return customers;
    return customers.filter(c =>
      (c.name || "").toLowerCase().includes(term) ||
      (c.email || "").toLowerCase().includes(term) ||
      (c.organization_name || "").toLowerCase().includes(term) ||
      (c.sub_organization_name || "").toLowerCase().includes(term)
    );
  }, [q, customers]);

  const custAssets = useMemo(() => {
    if (!selected) return [];
    return assets.filter(a => (a.customer_email || "").toLowerCase() === (selected.email || "").toLowerCase());
  }, [selected, assets]);

  if (user && !["admin", "inspector"].includes(user.role)) return <Navigate to="/dashboard" replace />;

  return (
    <div className="p-6 md:p-8" data-testid="my-customers-page">
      <div className="mb-6">
        <div className="text-xs uppercase tracking-[0.2em] text-gray-500 mb-1">Book of Business</div>
        <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight">My Customers</h1>
        <p className="text-sm text-gray-500 mt-1">Customers and assets assigned to you as the responsible inspector.</p>
      </div>

      {loading ? (
        <div className="p-10 text-center text-gray-500 text-sm">Loading…</div>
      ) : customers.length === 0 ? (
        <div className="bg-white border border-gray-200 p-12 text-center">
          <div className="w-14 h-14 bg-gray-100 mx-auto flex items-center justify-center mb-3"><User2 className="w-6 h-6 text-gray-400" strokeWidth={2} /></div>
          <div className="font-display text-lg font-bold tracking-tight">No customers assigned yet</div>
          <p className="text-sm text-gray-500 mt-1">An administrator can assign you as the responsible inspector on the Users page.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Left: customers list */}
          <div className="bg-white border border-gray-200 self-start">
            <div className="px-4 py-3 border-b border-gray-200">
              <div className="relative">
                <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <Input value={q} onChange={e => setQ(e.target.value)} placeholder="Search customer, org…" className="rounded-md pl-8 h-9" data-testid="mc-search" />
              </div>
            </div>
            <div className="divide-y divide-gray-100 max-h-[70vh] overflow-y-auto" data-testid="mc-list">
              {filtered.map(c => {
                const active = selected?.id === c.id;
                return (
                  <button key={c.id} onClick={() => setSelected(c)} className={`w-full text-left px-4 py-3 hover:bg-gray-50 ${active ? "bg-[#F0F7FF] border-l-2 border-l-[#01ADEF]" : "border-l-2 border-l-transparent"}`} data-testid={`mc-row-${c.id}`}>
                    <div className="font-semibold truncate">{c.name || c.email}</div>
                    <div className="text-xs text-gray-500 truncate flex items-center gap-1"><Mail className="w-3 h-3" />{c.email}</div>
                    {(c.organization_name || c.sub_organization_name) && (
                      <div className="text-[11px] text-gray-500 truncate mt-1 flex items-center gap-1"><Building2 className="w-3 h-3" />{c.organization_name}{c.sub_organization_name ? ` · ${c.sub_organization_name}` : ""}</div>
                    )}
                    <div className="text-[10px] font-mono uppercase tracking-wider font-bold text-gray-500 mt-1"><Package className="w-3 h-3 inline mr-1" />{c.asset_count} asset{c.asset_count === 1 ? "" : "s"}</div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Right: selected customer details */}
          <div className="lg:col-span-2">
            {selected ? (
              <>
                <div className="bg-white border border-gray-200 mb-4">
                  <div className="px-5 py-4 border-b border-gray-200">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <div className="font-display text-xl font-bold tracking-tight" data-testid="mc-detail-name">{selected.name || selected.email}</div>
                        <div className="text-xs text-gray-500 flex items-center gap-1 mt-1"><Mail className="w-3 h-3" />{selected.email}</div>
                      </div>
                      <span className="text-[10px] font-mono font-bold uppercase tracking-wider bg-[#D1FAE5] text-[#065F46] border border-[#6EE7B7] px-2 py-1">Assigned to you</span>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-3 divide-y sm:divide-y-0 sm:divide-x divide-gray-100">
                    <MetaCell label="Organisation" value={selected.organization_name || "—"} icon={Building2} />
                    <MetaCell label="Sub-organisation" value={selected.sub_organization_name || "—"} icon={Building2} />
                    <MetaCell label="Total Assets" value={selected.asset_count} icon={Package} accent="text-[#01ADEF]" />
                  </div>
                </div>

                <div className="bg-white border border-gray-200 overflow-x-auto">
                  <div className="px-5 py-4 border-b border-gray-200 flex items-center justify-between">
                    <h2 className="font-display text-lg font-bold tracking-tight">Assets</h2>
                    <span className="text-[10px] font-mono font-bold uppercase tracking-wider text-gray-500">{custAssets.length}</span>
                  </div>
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-gray-200 bg-gray-50">
                        <Th>Name</Th><Th>Tag</Th><Th>Category</Th><Th>Location</Th><Th>Status</Th><Th></Th>
                      </tr>
                    </thead>
                    <tbody data-testid="mc-assets-table">
                      {custAssets.length === 0 ? (
                        <tr><td colSpan={6} className="text-center py-10 text-gray-500 text-sm">This customer has no assets yet.</td></tr>
                      ) : custAssets.map(a => (
                        <tr key={a.id} className="border-b border-gray-100 hover:bg-gray-50" data-testid={`mc-asset-${a.id}`}>
                          <Td className="font-semibold">{a.name}</Td>
                          <Td className="font-mono text-xs">{a.asset_tag}</Td>
                          <Td className="capitalize">{(a.category || "").replace("_", " ")}</Td>
                          <Td>{a.location}</Td>
                          <Td><StatusPill v={a.status} /></Td>
                          <Td>
                            <Link to={`/assets`} className="text-[#01ADEF] hover:text-[#0198D6] inline-flex items-center gap-1 text-xs font-semibold" data-testid={`mc-open-asset-${a.id}`}>
                              Open <ExternalLink className="w-3 h-3" />
                            </Link>
                          </Td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            ) : (
              <div className="bg-white border border-gray-200 p-10 text-center text-gray-500 text-sm">Select a customer to see details.</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

const Th = ({ children }) => <th className="text-left px-4 py-3 text-xs uppercase tracking-wider font-semibold text-gray-600">{children}</th>;
const Td = ({ children, className = "" }) => <td className={`px-4 py-3 ${className}`}>{children}</td>;
const MetaCell = ({ label, value, icon: Icon, accent = "" }) => (
  <div className="p-4">
    <div className="flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold text-gray-500 mb-1"><Icon className="w-3 h-3" />{label}</div>
    <div className={`font-display text-lg font-bold tracking-tight ${accent}`}>{value}</div>
  </div>
);
const StatusPill = ({ v }) => {
  const map = { active: ["bg-[#D1FAE5]", "text-[#065F46]", "border-[#6EE7B7]", "Active"], out_of_service: ["bg-[#FEF3C7]", "text-[#92400E]", "border-[#FCD34D]", "Out"], retired: ["bg-gray-100", "text-gray-700", "border-gray-300", "Retired"] };
  const [bg, tx, br, l] = map[v] || map.active;
  return <span className={`inline-block text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 border ${bg} ${tx} ${br}`}>{l}</span>;
};
